/**
 * PriorityBadge — visual treatment for a task priority. Reuses the
 * fault-severity colour model via PRIORITY_THEME. Three variants:
 *  - 'badge' — icon + label pill (default)
 *  - 'icon'  — glyph only (tight rows)
 *  - 'dot'   — a coloured dot (ultra-compact)
 */

import type { TaskPriority } from '@/types/schema';
import { getPriorityTheme } from '@/config/entityThemes';

interface PriorityBadgeProps {
  priority?: TaskPriority | null;
  variant?: 'badge' | 'icon' | 'dot';
  className?: string;
}

export function PriorityBadge({ priority, variant = 'badge', className = '' }: PriorityBadgeProps) {
  const theme = getPriorityTheme(priority);
  const Icon = theme.Icon;

  if (variant === 'dot') {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${theme.dot} ${className}`}
        title={`${theme.label} priority`}
        aria-label={`${theme.label} priority`}
      />
    );
  }

  if (variant === 'icon') {
    return (
      <span title={`${theme.label} priority`} aria-label={`${theme.label} priority`}>
        <Icon className={`w-3.5 h-3.5 ${theme.text} ${className}`} strokeWidth={2.4} />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${theme.badge} ${className}`}
    >
      <Icon className="w-3 h-3 shrink-0" strokeWidth={2.4} />
      {theme.label}
    </span>
  );
}
